/**
 * EntityImage — renders the most-recent image attachment for an entity.
 *
 * Used by the v2 cards in place of CardThumbnailFallback. Fetches once
 * per (entity_type, entity_id) on mount; on cache miss falls back to the
 * brand-mark fallback so the layout never collapses.
 *
 * Loading state is intentionally just the fallback (rather than a spinner
 * or skeleton) — the lookup is fast on a cache hit and an evaporating
 * spinner is more visually noisy than the static brand mark.
 */
import { useEffect, useState } from 'react';
import { CardThumbnailFallback } from '@/components/v2/Card';
import { listAttachments, getAttachmentDownloadUrl } from '@/lib/attachments';

interface EntityImageProps {
  entityType: string;
  entityId: string;
  className?: string;
  alt?: string;
}

export function EntityImage({ entityType, entityId, className, alt }: EntityImageProps): JSX.Element {
  const [src, setSrc] = useState<string | null>(null);
  const [errored, setErrored] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setErrored(false);
    setSrc(null);
    listAttachments({ linked_entity_type: entityType, linked_entity_id: entityId, image_only: true })
      .then((items) => {
        if (cancelled) return;
        if (items.length > 0) setSrc(getAttachmentDownloadUrl(items[0].id));
      })
      .catch(() => {
        if (!cancelled) setErrored(true);
      });
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId]);

  if (!src || errored) {
    return <CardThumbnailFallback className={className} />;
  }
  return (
    <div className={`flex aspect-[4/3] w-full items-center justify-center overflow-hidden bg-sand dark:bg-surface-raised-dark ${className ?? ''}`}>
      <img
        src={src}
        alt={alt ?? ''}
        className="h-full w-full object-cover"
        onError={() => setErrored(true)}
        loading="lazy"
      />
    </div>
  );
}
